import { Fragment, useEffect, useMemo, useState } from "react";
import { formatUzPhone } from "../phone";
import { FinanceAllocation } from "../components/finance-allocation";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ChevronDown,
  ChevronRight,
  CreditCard,
  History,
  Search,
  Send,
  Settings,
  Wallet,
} from "lucide-react";
import {
  api,
  type DebtClient,
  type DebtClientHistory,
  type DebtNamedDebtor,
  type DebtNamedDebtorHistory,
  type DebtSummary,
  type DebtLedgerOut,
  type DebtLedgerRow,
  type DebtTransaction,
  type PaymentStatus,
} from "../api";
import { downloadFile } from "../api";
import { fmtDateTime, fmtMoney } from "../lib/format";
import { useFeature } from "../lib/use-tariff";
import { ExportButton } from "../components/export-button";
import { ConfirmDialog } from "../components/confirm-dialog";
import { InfoTooltip } from "../components/info-tooltip";
import { PaymentEditDialog } from "../components/payment-edit-dialog";
import {
  DebtPaymentDialog,
  type DebtPaymentTarget,
} from "../components/debt-payment-dialog";
import { usePaymentMethodLabel } from "../components/payment-method-select";
import { usePageHeader } from "../lib/use-page-header";
import { Money, Mono, Plate, StatusChip } from "../components/ui/ledger";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  DebtReminderSettingsDialog,
  type DebtReminderSettings,
} from "../components/debt-reminder-settings-dialog";
import { EmptyState } from "../components/ui/empty-state";
import { Skeleton } from "../components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

const SOURCE_KEYS: Record<string, string> = {
  work: "source.work",
  sale: "source.sale",
  purchase: "source.purchase",
  qolda: "source.qolda",
};

const STATUS_TONE: Record<PaymentStatus, "danger" | "warn" | "ok"> = {
  tolanmagan: "danger",
  qisman: "warn",
  tolandi: "ok",
};

const STATUS_KEY: Record<PaymentStatus, string> = {
  tolanmagan: "status.qarz",
  qisman: "status.qisman",
  tolandi: "status.tolandi",
};

// Bitta yuqori tab paneli mijoz balanslari, hujjat provenansi va tarixni
// boshqaradi. Qo'lda harakatlar mijozning Tarix dialogida saqlanib qoladi.
const TABS = ["clients", "sale", "purchase", "work", "tarix"] as const;
// Omborsiz tarifda (masalan comfort) sotuv/xarid hujjatlari umuman mavjud
// bo'lolmaydi — sahifa sof mijoz-qarz daftari bo'lib ko'rinadi: tab ham,
// kreditorlik bo'limi ham, "to'lanadigan" kartasi ham chiqmaydi. Gate `tovars`
// bayrog'ida (tarif nomida emas): backend ham xuddi shu chiziqni tortadi
// (`?manba=purchase|sale` omborsiz tarifda 403).
const TABS_NO_INVENTORY = ["clients", "work", "tarix"] as const;

type DetailLine = {
  nomi: string;
  miqdor?: string;
  narx?: string;
  jami: string;
};

/** Qator kengaytirilganda — qarz sababi (tovar/xizmat) tafsiloti. */
function LedgerDetail({ row }: { row: DebtLedgerRow }) {
  const { t } = useTranslation("qarzlar");
  const [lines, setLines] = useState<DetailLine[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      const id = row.source_ref_id;
      const out: DetailLine[] = [];
      try {
        if (id != null && row.source_type === "sale") {
          const s = await api.getSale(id);
          for (const l of s.lines)
            out.push({ nomi: l.product_nomi, miqdor: l.miqdor, narx: l.birlik_narx, jami: l.jami_summa });
        } else if (id != null && row.source_type === "purchase") {
          const p = await api.getPurchase(id);
          for (const l of p.lines)
            out.push({ nomi: l.product_nomi, miqdor: l.miqdor, narx: l.birlik_narx, jami: l.jami_summa });
        } else if (id != null && row.source_type === "work") {
          const w = await api.getWork(id);
          if (Number(w.price) > 0)
            out.push({ nomi: w.service_name || t("detail.ishHaqi"), jami: w.price });
          for (const l of w.parts)
            out.push({ nomi: l.product_nomi, miqdor: l.miqdor, narx: l.birlik_narx, jami: l.jami_summa });
        }
        if (alive) setLines(out);
      } catch {
        if (alive) setLines([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [row.source_type, row.source_ref_id, t]);

  if (loading) {
    return (
      <div className="space-y-2 p-3">
        <Skeleton className="h-7" />
        <Skeleton className="h-7" />
      </div>
    );
  }
  if (!lines || lines.length === 0) {
    return <div className="px-4 py-3 text-sm text-muted-foreground">{t("detail.empty")}</div>;
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("detail.tovar")}</TableHead>
          <TableHead className="text-right">{t("detail.miqdor")}</TableHead>
          <TableHead className="text-right">{t("detail.narx")}</TableHead>
          <TableHead className="text-right">{t("detail.jami")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {lines.map((l, i) => (
          <TableRow key={i}>
            <TableCell className="font-medium">{l.nomi}</TableCell>
            <TableCell className="text-right tabular-nums">
              {l.miqdor != null ? Number(l.miqdor) : "—"}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {l.narx != null ? fmtMoney(l.narx) : "—"}
            </TableCell>
            <TableCell className="text-right tabular-nums">{fmtMoney(l.jami)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** Bitta birlashtirilgan qarz daftari jadvali (debitorlik yoki kreditorlik). */
function LedgerTable({
  rows,
  counterpartyHeader,
  onRefresh,
  readOnly = false,
}: {
  rows: DebtLedgerRow[];
  counterpartyHeader: string;
  onRefresh: () => void;
  /** "Tarix" — yopilgan qarzlar: amal tugmalari yo'q, qoldiq o'rniga jami. */
  readOnly?: boolean;
}) {
  const { t } = useTranslation("qarzlar");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [payFor, setPayFor] = useState<DebtLedgerRow | null>(null);

  if (rows.length === 0) {
    return (
      <EmptyState
        icon={<Wallet />}
        title={t("ledger.emptyTitle")}
        description={t("ledger.emptyDescription")}
      />
    );
  }

  async function savePayment(
    row: DebtLedgerRow,
    body: { tolov_holati: PaymentStatus; tolangan_summa?: string | null },
  ) {
    const id = row.source_ref_id;
    if (id == null) return;
    if (row.source_type === "sale") await api.updateSalePayment(id, body);
    else if (row.source_type === "purchase") await api.updatePurchasePayment(id, body);
    else if (row.source_type === "work") await api.updateWorkPayment(id, body);
    onRefresh();
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8" />
            <TableHead>{t("ledger.date")}</TableHead>
            <TableHead>{t("ledger.source")}</TableHead>
            <TableHead>{counterpartyHeader}</TableHead>
            <TableHead className="text-right">{t("ledger.jami")}</TableHead>
            <TableHead className="text-right">{t("ledger.tolangan")}</TableHead>
            <TableHead className="text-right">{t("ledger.qoldiq")}</TableHead>
            <TableHead>{t("ledger.status")}</TableHead>
            {!readOnly && <TableHead className="text-right">{t("table.actions")}</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r, i) => {
            const key = `${r.source_type}-${r.source_ref_id}-${i}`;
            const open = expanded === key;
            // Qo'lda kiritilgan daftar harakati — hujjat emas: "qoldiq"/"jami"
            // tushunchasi yo'q, hujjat to'lovi dialogi ham ochilmaydi.
            const isManual = r.source_type === "qolda";
            // Qarzdorning umumiy balansi yopilgan → bu qator TARIX.
            const settled = r.hisob_yopilgan;
            return (
              <Fragment key={key}>
                <TableRow
                  className={`cursor-pointer${settled ? " opacity-60" : ""}`}
                  onClick={() => setExpanded(open ? null : key)}
                >
                  <TableCell className="text-muted-foreground [&_svg]:size-4">
                    {open ? <ChevronDown /> : <ChevronRight />}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <Mono className="text-muted-foreground">{fmtDateTime(r.created_at)}</Mono>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {t(SOURCE_KEYS[r.source_type] ?? r.source_type)}
                  </TableCell>
                  <TableCell className="font-medium">
                    <span className="flex items-center gap-2">
                      {r.counterparty_name || t("ledger.anonymous")}
                      {settled && (
                        <StatusChip tone="ok">{t("ledger.settled")}</StatusChip>
                      )}
                    </span>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {isManual ? "—" : fmtMoney(r.jami)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {isManual ? (
                      "—"
                    ) : (
                      <Money tone="pos">
                        {fmtMoney(Number(r.jami) - Number(r.summa))}
                      </Money>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {/* Qo'lda harakatda bu — qoldiq emas, harakatning O'ZI:
                        to'lov (pul kirdi) yoki uni bekor qilish. */}
                    <Money
                      tone={
                        isManual
                          ? r.tur === "tolov"
                            ? "pos"
                            : "neg"
                          : Number(r.summa) > 0
                            ? "neg"
                            : undefined
                      }
                    >
                      {isManual && r.tur === "tolov" ? "−" : ""}
                      {fmtMoney(r.summa)}
                    </Money>
                  </TableCell>
                  <TableCell>
                    <StatusChip tone={STATUS_TONE[r.holat]}>{t(STATUS_KEY[r.holat])}</StatusChip>
                  </TableCell>
                  {!readOnly && (
                    <TableCell className="text-right whitespace-nowrap">
                      {!isManual && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPayFor(r);
                          }}
                        >
                          {t("actions.payment")}
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
                {open && (
                  <TableRow>
                    <TableCell colSpan={readOnly ? 8 : 9} className="bg-muted/30 p-0">
                      <LedgerDetail row={r} />
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>

      {payFor && (
        <PaymentEditDialog
          open={!!payFor}
          onOpenChange={(o) => !o && setPayFor(null)}
          jami={Number(payFor.jami)}
          initialPaid={Number(payFor.jami) - Number(payFor.summa)}
          partyLabel={payFor.counterparty_name || undefined}
          onSave={(body) => savePayment(payFor, body)}
        />
      )}
    </>
  );
}

export function QarzlarPage() {
  const { t } = useTranslation("qarzlar");
  const [clients, setClients] = useState<DebtClient[]>([]);
  const [ledger, setLedger] = useState<DebtLedgerOut | null>(null);
  // "Tarix" tabi — faqat ochilganda yuklanadi (lazy), serverga keraksiz yuk bermaslik uchun.
  const [history, setHistory] = useState<DebtLedgerOut | null>(null);
  const [tab, setTab] = useState<string>("clients");
  const [clientSearch, setClientSearch] = useState("");
  // Ombor bayrog'i sotuv/xarid manbali yuzalarni boshqaradi (tarif nomi emas).
  const hasInventory = useFeature("tovars");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);

  const [historyFor, setHistoryFor] = useState<DebtClient | null>(null);
  // Mijoz balanslari yig'indisi — `ledger.jami_debitorlik` dan ATAYLAB boshqa
  // bazaga qurilgan (u ochiq hujjatlar qoldig'i). Qo'lda kiritilgan to'lov
  // faqat shuni harakatlantiradi, shuning uchun alohida karta kerak.
  const [summary, setSummary] = useState<DebtSummary | null>(null);
  const [debtors, setDebtors] = useState<DebtNamedDebtor[]>([]);
  const [debtorHistoryFor, setDebtorHistoryFor] = useState<string | null>(null);
  const [payTarget, setPayTarget] = useState<
    { target: DebtPaymentTarget; balans: string } | null
  >(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [reminderSettings, setReminderSettings] = useState<DebtReminderSettings | null>(null);
  const [messagePreview, setMessagePreview] = useState<string | null>(null);

  async function openSettings() {
    setReminderSettings(await api.debtReminderSettings());
    setSettingsOpen(true);
  }

  // Eksport tugmasi — yuqori panelda (Max upsell ExportButton ichida).
  const actions = useMemo(
    () => (
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={() => void openSettings()}><Settings />{t("reminder.settings")}</Button>
        <ExportButton onExport={() => downloadFile("/debts/export.xlsx", "qarz-daftar.xlsx")} />
      </div>
    ),
    [t],
  );
  usePageHeader({ actions });

  // Birlashtirilgan daftar — yangilanishda qayta o'qiladi.
  useEffect(() => {
    (async () => {
      try {
        setLedger(await api.debtLedger("all"));
        setErr(null);
      } catch (e) {
        setErr((e as { message?: string }).message || t("error"));
      }
    })();
  }, [refresh]);

  // Mijoz balanslari: balans, harakatlar tarixi VA to'lov qabul qilish.
  // To'lov mijoz BALANSIGA yoziladi (hujjatga emas) — hujjat bo'yicha to'lov
  // esa yuqoridagi daftar jadvalidagi "To'lov" tugmasi orqali qoladi.
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [cl, sm, dz] = await Promise.all([
          api.debtClients(),
          api.debtSummary(),
          api.debtNamedDebtors(),
        ]);
        setClients(cl);
        setSummary(sm);
        setDebtors(dz);
      } catch (e) {
        setErr((e as { message?: string }).message || t("error"));
      } finally {
        setLoading(false);
      }
    })();
  }, [refresh]);

  // Tarix faqat tab ochilganda yuklanadi; yangilanishda qaytadan o'qiladi.
  useEffect(() => {
    if (tab !== "tarix") return;
    let alive = true;
    (async () => {
      try {
        const data = await api.debtLedger("all", true);
        if (alive) setHistory(data);
      } catch (e) {
        if (alive) setErr((e as { message?: string }).message || t("error"));
      }
    })();
    return () => {
      alive = false;
    };
  }, [tab, refresh, t]);

  const onRefresh = () => setRefresh((x) => x + 1);

  const rows = ledger?.rows ?? [];
  // Xarid (kirim) qatorlari = kreditorlik (biz qarzdormiz); qolgani debitorlik.
  const creditRows = rows.filter((r) => r.source_type === "purchase");
  const debitAll = rows.filter((r) => r.source_type !== "purchase");
  const debitRows = debitAll.filter((r) => r.source_type === tab);

  const normalizeSearch = (value: string) =>
    value.toLocaleLowerCase().replace(/[^\p{L}\p{N}]/gu, "");
  const normalizedSearch = normalizeSearch(clientSearch);
  const filteredClients = clients.filter((client) => {
    if (!normalizedSearch) return true;
    return [client.ism, client.telefon, ...client.plates].some((value) =>
      normalizeSearch(value).includes(normalizedSearch),
    );
  });

  // Tab tanlovi ikki jadval ko'rinishini boshqaradi (ikki jadval saqlanadi).
  const showDebit = tab === "sale" || tab === "work";
  const showCredit = hasInventory && tab === "purchase";
  const tabs = hasInventory ? TABS : TABS_NO_INVENTORY;

  const ledgerSkeleton = (
    <div className="p-4 space-y-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-12" />
      ))}
    </div>
  );

  return (
    <div className="debts-page space-y-4">
      {err && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3 text-sm">{err}</div>
      )}

      {/* Summary kartalar — debitorlik (bizga qarz), mijoz balanslari va,
          ombor bo'lsa, kreditorlik (biz qarzdormiz).

          BITTA debitorlik raqami — daftar netidan. Ilgari bu yerda IKKITA
          karta bor edi (biri ochiq hujjat qoldiqlarining yig'indisi) va ular
          bir-biriga ham, bosh sahifaga ham mos kelmasdi: balansga yozilgan
          to'lov netni harakatlantirib, hujjat qoldig'ini qoldirardi. Ikki
          mustaqil hisobni kelishtirib bo'lmaydi, shuning uchun net yagona
          haqiqat qilindi va hujjatlar pastda PROVENANS bo'lib qoldi. */}
      <div className={hasInventory ? "debt-summary-grid grid gap-4 sm:grid-cols-2" : "debt-summary-grid grid gap-4"}>
        <Card className="debt-summary-card debt-summary-card--receivable bg-gradient-to-br from-destructive to-destructive/80 text-destructive-foreground border-destructive shadow-md">
          <CardContent className="p-5 space-y-2">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1 text-xs uppercase tracking-wider font-semibold opacity-90">
                {t("summary.total")}
                <InfoTooltip text={t("summary.totalHint")} />
              </span>
              <span className="size-8 rounded-md bg-white/15 flex items-center justify-center [&_svg]:size-4">
                <Wallet />
              </span>
            </div>
            <div className="font-num text-3xl font-bold tracking-tight tabular-nums">
              {fmtMoney(ledger?.jami_debitorlik ?? summary?.jami_debitorlik ?? "0")}
            </div>
            {summary != null && (
              <div className="text-xs opacity-90">
                {t("summary.debtorCount", { count: summary.qarzdor_mijozlar_soni })}
              </div>
            )}
          </CardContent>
        </Card>

        {hasInventory && (
          <Card className="debt-summary-card debt-summary-card--payable">
            <CardContent className="p-5 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t("summary.kreditorlik")}</span>
                <span className="size-8 rounded-md bg-muted text-muted-foreground flex items-center justify-center [&_svg]:size-4">
                  <CreditCard />
                </span>
              </div>
              <div className="font-num text-3xl font-bold tracking-tight tabular-nums text-warning">{fmtMoney(ledger?.jami_kreditorlik ?? "0")}</div>
            </CardContent>
          </Card>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          {tabs.map((value) => (
            <TabsTrigger key={value} value={value}>
              {value === "clients"
                ? t("filter.clients")
                : value === "tarix"
                  ? t("filter.tarix")
                  : t(SOURCE_KEYS[value])}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Mijozlar bo'yicha balanslar — to'lov kiritish va tarix. */}
      {tab === "clients" && <div className="debt-clients-section pt-2 space-y-2">
        <h2 className="text-sm font-semibold text-muted-foreground">{t("clients.title")}</h2>
        <div className="relative max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            value={clientSearch}
            onChange={(event) => setClientSearch(event.target.value)}
            placeholder={t("clients.searchPlaceholder")}
            aria-label={t("clients.searchPlaceholder")}
          />
        </div>
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-4 space-y-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-12" />
                ))}
              </div>
            ) : clients.length === 0 ? (
              <EmptyState
                icon={<Wallet />}
                title={t("empty.title")}
                description={t("empty.description")}
              />
            ) : filteredClients.length === 0 ? (
              <EmptyState
                icon={<Search />}
                title={t("clients.searchEmpty")}
                description={t("clients.searchEmptyDescription")}
              />
            ) : (
              <div className="overflow-x-auto"><Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("table.client")}</TableHead>
                    <TableHead>{t("table.phone")}</TableHead>
                    <TableHead>{t("table.plates")}</TableHead>
                    <TableHead className="text-right">{t("table.debt")}</TableHead>
                    <TableHead>{t("table.lastAction")}</TableHead>
                    <TableHead>{t("table.reminderStatus")}</TableHead>
                    <TableHead>{t("table.message")}</TableHead>
                    <TableHead className="text-right">{t("table.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredClients.map((c) => (
                    <TableRow key={c.client_id}>
                      <TableCell className="font-medium">{c.ism}</TableCell>
                      <TableCell>
                        <Mono className="text-muted-foreground">{formatUzPhone(c.telefon)}</Mono>
                      </TableCell>
                      <TableCell>
                        {c.plates.length === 0 ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {c.plates.map((plate) => <Plate key={plate}>{plate}</Plate>)}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Money tone="neg">{fmtMoney(c.balans)}</Money>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <Mono className="text-muted-foreground">
                          {c.oxirgi_harakat ? fmtDateTime(c.oxirgi_harakat) : "—"}
                        </Mono>
                      </TableCell>
                      <TableCell><StatusChip tone={c.reminder_status === "paid" ? "ok" : c.reminder_status === "telegram_unlinked" ? "danger" : "warn"}>{t(`reminder.status.${c.reminder_status}`)}</StatusChip></TableCell>
                      <TableCell className="max-w-[180px]"><button className="block max-w-[180px] truncate text-left text-sm text-muted-foreground hover:text-foreground" onClick={() => setMessagePreview(c.reminder_text)}>{c.reminder_text}</button></TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setPayTarget({
                              target: {
                                kind: "client",
                                clientId: c.client_id,
                                name: c.ism,
                              },
                              balans: c.balans,
                            })
                          }
                        >
                          <Wallet />
                          {t("actions.payment")}
                        </Button>
                        <Button variant="ghost" size="sm" disabled={!c.telegram_linked} onClick={async () => {
                          await api.sendDebtReminder(c.client_id, crypto.randomUUID());
                          toast.success(t("reminder.sent"));
                          onRefresh();
                        }}><Send />{t("reminder.sendNow")}</Button>
                        <Button variant="ghost" size="sm" onClick={() => setHistoryFor(c)}>
                          <History />
                          {t("actions.history")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table></div>
            )}
          </CardContent>
        </Card>
      </div>}

      {/* Nomli qarzdorlar — kartochkasiz, anonim kredit sotuvdan tug'iladi.
          Ular `GET /debts/clients` da KO'RINMAYDI (u Client bilan INNER JOIN
          qiladi), shuning uchun alohida jadval. Bo'sh bo'lsa umuman
          chizilmaydi — bunday qarzdori yo'q ustaxona uchun ekran o'zgarmaydi. */}
      {tab === "clients" && debtors.length > 0 && (
        <div className="pt-2 space-y-2">
          <h2 className="text-sm font-semibold text-muted-foreground">
            {t("namedDebtors.title")}
          </h2>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("namedDebtors.name")}</TableHead>
                    <TableHead className="text-right">{t("table.balance")}</TableHead>
                    <TableHead>{t("table.lastAction")}</TableHead>
                    <TableHead className="text-right">{t("table.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {debtors.map((d) => (
                    <TableRow key={d.debtor_name}>
                      <TableCell className="font-medium">{d.debtor_name}</TableCell>
                      <TableCell className="text-right">
                        <Money tone="neg">{fmtMoney(d.balans)}</Money>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <Mono className="text-muted-foreground">
                          {d.oxirgi_harakat ? fmtDateTime(d.oxirgi_harakat) : "—"}
                        </Mono>
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setPayTarget({
                              target: { kind: "debtor", name: d.debtor_name },
                              balans: d.balans,
                            })
                          }
                        >
                          <Wallet />
                          {t("actions.payment")}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDebtorHistoryFor(d.debtor_name)}
                        >
                          <History />
                          {t("actions.history")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── Hujjatlar: qarz QAYERDAN kelgan ──────────────────────────

          Bu ro'yxat PROVENANS. Undagi qoldiqlar — hujjat qanday yozilgan
          bo'lsa shundayligicha, ya'ni TARIX; ularni qo'shib "jami qarz"
          chiqarilmaydi (yuqoridagi karta daftar netidan keladi). Balans
          bo'yicha hisoblashgan qarzdorning qatori "yopilgan" deb
          belgilanadi — aks holda ega uni ochiq talab deb o'qirdi. */}
      {tab !== "clients" && <div className="pt-2 space-y-2">
        <h2 className="flex items-center gap-1 text-sm font-semibold text-muted-foreground">
          {t("provenance.title")}
          <InfoTooltip text={t("provenance.hint")} />
        </h2>
      </div>}

      {/* Debitorlik — bizga qarzdorlar (sotuv/ish). */}
      {showDebit && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-muted-foreground">{t("ledger.debitorlik_title")}</h2>
          <Card>
            <CardContent className="p-0">
              {ledger === null ? ledgerSkeleton : (
                <LedgerTable rows={debitRows} counterpartyHeader={t("ledger.debtor")} onRefresh={onRefresh} />
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Kreditorlik — biz qarzdor bo'lgan yetkazib beruvchilar (xarid). */}
      {showCredit && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-muted-foreground">{t("ledger.kreditorlik_title")}</h2>
          <Card>
            <CardContent className="p-0">
              {ledger === null ? ledgerSkeleton : (
                <LedgerTable rows={creditRows} counterpartyHeader={t("ledger.creditor")} onRefresh={onRefresh} />
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tarix — yopilgan (to'liq to'langan) qarzlar, faqat ko'rish uchun. */}
      {tab === "tarix" && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-muted-foreground">{t("ledger.tarix_title")}</h2>
          <Card>
            <CardContent className="p-0">
              {history === null ? (
                ledgerSkeleton
              ) : (
                <LedgerTable
                  rows={history.rows}
                  counterpartyHeader={t("ledger.debtor")}
                  onRefresh={onRefresh}
                  readOnly
                />
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <Dialog open={!!historyFor} onOpenChange={(o) => !o && setHistoryFor(null)}>
        {historyFor && (
          <HistoryView clientId={historyFor.client_id} onChanged={onRefresh} />
        )}
      </Dialog>

      <Dialog
        open={!!debtorHistoryFor}
        onOpenChange={(o) => !o && setDebtorHistoryFor(null)}
      >
        {debtorHistoryFor && (
          <DebtorHistoryView nom={debtorHistoryFor} onChanged={onRefresh} />
        )}
      </Dialog>

      <DebtReminderSettingsDialog
        open={settingsOpen}
        settings={reminderSettings}
        onOpenChange={setSettingsOpen}
        onChange={setReminderSettings}
        onSave={async () => {
          if (!reminderSettings) return;
          const saved = await api.updateDebtReminderSettings(reminderSettings);
          setReminderSettings(saved);
          setSettingsOpen(false);
          toast.success(t("reminder.saved"));
        }}
      />
      <Dialog open={messagePreview !== null} onOpenChange={(o) => !o && setMessagePreview(null)}><DialogHeader><DialogTitle>{t("reminder.fullMessage")}</DialogTitle></DialogHeader><DialogBody className="whitespace-pre-wrap text-sm">{messagePreview}</DialogBody></Dialog>

      {payTarget && (
        <DebtPaymentDialog
          target={payTarget.target}
          balans={payTarget.balans}
          onClose={() => setPayTarget(null)}
          onSaved={() => {
            setPayTarget(null);
            onRefresh();
          }}
        />
      )}
    </div>
  );
}

const MANBA_KEYS: Record<string, string> = {
  work: "source.work",
  sale: "source.sale",
  purchase: "source.purchase",
  qolda: "source.qolda",
};

/**
 * Qarz harakatlari jadvali — mijoz va nomli qarzdor tarixida BIR XIL.
 *
 * Qo'lda kiritilgan to'lovni (`manba=qolda`, `tur=tolov`, `manba_id=null`)
 * bekor qilish mumkin. Hujjatdan tug'ilgan qatorlar (ish/sotuv/xarid) va
 * qaytarish qatorlari (`manba_id` to'lgan) bekor qilinmaydi — ularni server
 * ham rad etadi, bu yerda tugma shunchaki ko'rsatilmaydi.
 */
function HarakatlarTable({
  rows,
  onChanged,
}: {
  rows: DebtTransaction[];
  onChanged: () => void;
}) {
  const { t } = useTranslation(["qarzlar", "common"]);
  const usulLabel = usePaymentMethodLabel();
  const [bekorFor, setBekorFor] = useState<DebtTransaction | null>(null);

  // Allaqachon bekor qilingan to'lovlar: teskari yozuv `manba_id` da asl
  // to'lov id'sini saqlaydi. Server baribir 409 qaytaradi — bu faqat UI.
  const reversed = useMemo(
    () =>
      new Set(
        rows
          .filter((r) => r.tur === "qarz" && r.manba === "qolda" && r.manba_id != null)
          .map((r) => r.manba_id as number),
      ),
    [rows],
  );

  const canReverse = (r: DebtTransaction) =>
    r.tur === "tolov" && r.manba === "qolda" && r.manba_id == null;

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("history.date")}</TableHead>
            <TableHead>{t("history.type")}</TableHead>
            <TableHead>{t("history.source")}</TableHead>
            <TableHead className="text-right">{t("history.summa")}</TableHead>
            <TableHead>{t("common:paymentMethod.label")}</TableHead>
            <TableHead className="text-right">{t("history.actions")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id}>
              <TableCell className="whitespace-nowrap">
                <Mono className="text-muted-foreground">{fmtDateTime(row.created_at)}</Mono>
              </TableCell>
              <TableCell>
                {row.tur === "qarz" ? (
                  <StatusChip tone="danger">{t("history.debt")}</StatusChip>
                ) : (
                  <StatusChip tone="ok">{t("history.payment")}</StatusChip>
                )}
              </TableCell>
              <TableCell className="text-muted-foreground">{MANBA_KEYS[row.manba] ? t(MANBA_KEYS[row.manba]) : row.manba}</TableCell>
              <TableCell className="text-right">
                <Money tone={row.tur === "qarz" ? "neg" : "pos"}>{fmtMoney(row.summa)}</Money>
              </TableCell>
              {/* Har bir to'lovning O'Z usuli shu yerda saqlanadi — ish
                  qisman naqd, qolgani karta bilan yopilgan bo'lsa ikkalasi
                  ham ko'rinadi. Qarz qatorida pul harakat qilmagan → "—". */}
              <TableCell className="whitespace-nowrap text-muted-foreground">
                {usulLabel(row.tolov_usuli)}
              </TableCell>
              <TableCell className="text-right whitespace-nowrap">
                {canReverse(row) &&
                  (reversed.has(row.id) ? (
                    <span className="text-xs text-muted-foreground">
                      {t("bekor.badge")}
                    </span>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setBekorFor(row)}
                    >
                      {t("bekor.action")}
                    </Button>
                  ))}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <ConfirmDialog
        open={!!bekorFor}
        onOpenChange={(o) => !o && setBekorFor(null)}
        title={t("bekor.title")}
        description={t("bekor.description")}
        confirmLabel={t("bekor.action")}
        destructive
        onConfirm={async () => {
          if (!bekorFor) return;
          try {
            await api.debtTolovBekor(bekorFor.id);
            toast.success(t("bekor.done"));
            setBekorFor(null);
            onChanged();
          } catch (e) {
            toast.error((e as { message?: string }).message || t("common:common.error"));
            throw e; // oyna ochiq qoladi
          }
        }}
      />
    </>
  );
}

function HistoryView({
  clientId,
  onChanged,
}: {
  clientId: number;
  onChanged: () => void;
}) {
  const { t } = useTranslation(["qarzlar", "common"]);
  const [data, setData] = useState<DebtClientHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        setData(await api.debtClient(clientId));
      } finally {
        setLoading(false);
      }
    })();
  }, [clientId, reload]);

  return (
    <>
      <DialogHeader>
        <DialogTitle>{data ? data.ism : t("history.title")}</DialogTitle>
        <DialogDescription>
          {data ? t("history.balance", { amount: fmtMoney(data.balans), phone: formatUzPhone(data.telefon) }) : t("history.loading")}
        </DialogDescription>
      </DialogHeader>
      <DialogBody>
        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10" />)}
          </div>
        ) : !data || data.harakatlar.length === 0 ? (
          <EmptyState icon={<History />} title={t("history.emptyTitle")} description={t("history.emptyDescription")} />
        ) : (
          <HarakatlarTable
            rows={data.harakatlar}
            onChanged={() => {
              setReload((x) => x + 1);
              onChanged();
            }}
          />
        )}
        <FinanceAllocation key={clientId} clientId={clientId} />
      </DialogBody>
    </>
  );
}

function DebtorHistoryView({
  nom,
  onChanged,
}: {
  nom: string;
  onChanged: () => void;
}) {
  const { t } = useTranslation(["qarzlar", "common"]);
  const [data, setData] = useState<DebtNamedDebtorHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        setData(await api.debtNamedDebtorHistory(nom));
      } finally {
        setLoading(false);
      }
    })();
  }, [nom, reload]);

  return (
    <>
      <DialogHeader>
        <DialogTitle>{nom}</DialogTitle>
        <DialogDescription>
          {data
            ? t("history.balanceOnly", { amount: fmtMoney(data.balans) })
            : t("history.loading")}
        </DialogDescription>
      </DialogHeader>
      <DialogBody>
        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10" />)}
          </div>
        ) : !data || data.harakatlar.length === 0 ? (
          <EmptyState icon={<History />} title={t("history.emptyTitle")} description={t("history.emptyDescription")} />
        ) : (
          <HarakatlarTable
            rows={data.harakatlar}
            onChanged={() => {
              setReload((x) => x + 1);
              onChanged();
            }}
          />
        )}
      </DialogBody>
    </>
  );
}
